// Cadence math for the daily-SMS job's sending schedule, in the vocabulary the
// heartbeat service already speaks (TimingEntry in services/py/heartbeat/models.py).
// Pure module, no React — mirrors scheduleUtils.ts / budgetSlider.ts.

export type CadenceUnit = 'day' | 'week';

export interface Cadence {
	every: number;
	unit: CadenceUnit;
}

export interface TimingEntry {
	every: number;
	unit: CadenceUnit;
	at: string;
	timezone: string;
	tolerance_seconds: number;
	day_of_week?: string[];
}

// Coarse→fine in interval space. Sub-daily is absent by design: the daily send
// is deduped once per user per local date (api/routes/sms_send.py), so a second
// same-day fire is swallowed as a duplicate — a faster cadence would be a lie.
export const CADENCE_NOTCHES: readonly Cadence[] = [
	{ every: 1, unit: 'day' },
	{ every: 2, unit: 'day' },
	{ every: 3, unit: 'day' },
	{ every: 1, unit: 'week' },
	{ every: 2, unit: 'week' },
];

export const CADENCE_MIN_NOTCH = 0;
export const CADENCE_MAX_NOTCH = CADENCE_NOTCHES.length - 1;

// Lowercase full names are the only spelling the heartbeat accepts
// (services/py/heartbeat/models.py rejects "Monday" and "mon" alike). Order matters:
// this is indexed by Date.getDay(), which counts from Sunday.
export const WEEKDAYS = [
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

export function isWeekday(value: string): value is Weekday {
	return (WEEKDAYS as readonly string[]).includes(value);
}

export function weekdayOf(date: Date): Weekday {
	return WEEKDAYS[date.getDay()];
}

/** "monday" → "Monday". The wire format stays lowercase. */
export function formatWeekday(day: Weekday): string {
	return day.charAt(0).toUpperCase() + day.slice(1);
}

// Delay-only jitter (heartbeat/scheduling.py) is fed back in as the next rrule
// anchor, so on an every-N-days cadence a jittered fire that crosses local
// midnight re-phases the series a day early — measured collapsing "every other
// day" into daily for a late send time. Zero tolerance keeps every fire on the
// rrule grid, which is what makes the interval stable.
export const CADENCE_TOLERANCE_SECONDS = 0;

const clampNotch = (index: number): number =>
	Math.max(CADENCE_MIN_NOTCH, Math.min(CADENCE_MAX_NOTCH, Math.round(index)));

export function notchToCadence(index: number): Cadence {
	return CADENCE_NOTCHES[clampNotch(index)];
}

export function cadenceEquals(a: Cadence, b: Cadence): boolean {
	return a.every === b.every && a.unit === b.unit;
}

/**
 * Nearest notch for a stored cadence. A schedule written before this control
 * existed (or by a future one) still has to render, so an unrecognized shape
 * resolves to the closest notch by interval length rather than throwing.
 */
export function cadenceToNotch(cadence: Cadence): number {
	const exact = CADENCE_NOTCHES.findIndex((n) => cadenceEquals(n, cadence));
	if (exact !== -1) return exact;

	const days = cadenceIntervalDays(cadence);
	let nearest = CADENCE_MIN_NOTCH;
	let nearestDistance = Number.POSITIVE_INFINITY;
	CADENCE_NOTCHES.forEach((notch, i) => {
		const distance = Math.abs(cadenceIntervalDays(notch) - days);
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearest = i;
		}
	});
	return nearest;
}

export function cadenceIntervalDays(cadence: Cadence): number {
	return cadence.unit === 'week' ? cadence.every * 7 : cadence.every;
}

/** The hero readout — names the selected notch, so no notch-label strip is needed. */
export function formatCadence(cadence: Cadence): string {
	const { every, unit } = cadence;
	if (unit === 'day') {
		if (every === 1) return 'Every day';
		if (every === 2) return 'Every other day';
		return `Every ${every} days`;
	}
	if (every === 1) return 'Once a week';
	if (every === 2) return 'Every other week';
	return `Every ${every} weeks`;
}

/**
 * Per-option volume sublabel. Says something the interval label does not — how
 * much mail this actually means — so each row is comparable at rest instead of
 * only after it is selected.
 */
export function summarizeVolume(cadence: Cadence): string {
	const days = cadenceIntervalDays(cadence);
	if (days === 1) return 'About 7 texts a week';
	if (days < 7) return `About ${Math.round(7 / days)} texts a week`;
	if (days === 7) return '1 text a week';
	return `About ${Math.round(30 / days)} texts a month`;
}

/**
 * First-person consequence line, derived — never a literal map keyed by index.
 *
 * A weekly cadence names its weekday when one is known, because "once a week"
 * on its own leaves the reader unable to tell which day that is.
 */
export function describeCadence(cadence: Cadence, weekday?: Weekday): string {
	const { every, unit } = cadence;
	if (unit === 'day') {
		if (every === 1) return "I'll text you every morning.";
		if (every === 2) return "I'll text you every other day.";
		return `I'll text you every ${every} days.`;
	}
	if (!weekday) {
		if (every === 1) return "I'll text you once a week.";
		if (every === 2) return "I'll text you every other week.";
		return `I'll text you every ${every} weeks.`;
	}
	const day = formatWeekday(weekday);
	if (every === 1) return `I'll text you every ${day}.`;
	if (every === 2) return `I'll text you every other ${day}.`;
	return `I'll text you every ${every} weeks, on a ${day}.`;
}

/**
 * The weekday a weekly cadence sends on: whatever is already stored, else the
 * day the caller offers as a starting point.
 *
 * Unrecognized stored values are ignored rather than trusted — the control has
 * to render something selectable no matter what is in the schedule.
 */
export function resolveWeekday(
	storedDays: readonly string[] | null | undefined,
	fallback: Weekday,
): Weekday {
	return storedDays?.find(isWeekday) ?? fallback;
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidSendTime(at: string): boolean {
	return HHMM.test(at);
}

/**
 * The ONE place a TimingEntry is constructed, so the heartbeat's validation
 * rules hold in a single spot: `at` is required for day/week units, and
 * `day_of_week` is only legal when unit is "week".
 *
 * `timezone` is an explicit argument, never an ambient Intl lookup — the
 * browser rewrites the stored user timezone on every socket connect, and a
 * cadence the user set must not silently re-zone itself.
 *
 * `weekday` is required for weekly cadences and is likewise explicit rather
 * than read off the clock. Omit it and the heartbeat phases the series from the
 * moment Save happened, so "once a week" lands on a weekday nobody picked and
 * the caller has no way to name it back to the user.
 */
export function toTimingEntry(
	cadence: Cadence,
	at: string,
	timezone: string,
	weekday?: Weekday,
): TimingEntry {
	if (!isValidSendTime(at)) {
		throw new Error(`Invalid send time ${at}; expected HH:MM (24h)`);
	}
	if (cadence.unit === 'week' && !weekday) {
		throw new Error('A weekly cadence requires the weekday it sends on');
	}
	const entry: TimingEntry = {
		every: cadence.every,
		unit: cadence.unit,
		at,
		timezone,
		tolerance_seconds: CADENCE_TOLERANCE_SECONDS,
	};
	if (cadence.unit === 'week' && weekday) {
		entry.day_of_week = [weekday];
	}
	return entry;
}

const TWELVE_HOUR = new Intl.NumberFormat('en-US');

/** "9:00 AM" from "09:00". Presentation only; the wire format stays 24h. */
export function formatSendTime(at: string): string {
	const match = HHMM.exec(at);
	if (!match) return at;
	const hour24 = Number(match[1]);
	const minute = match[2];
	const suffix = hour24 < 12 ? 'AM' : 'PM';
	const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
	return `${TWELVE_HOUR.format(hour12)}:${minute} ${suffix}`;
}

/**
 * The confirmation line, rendered from the schedule's OWN next_fire_at.
 *
 * Deliberately not derived from (cadence, at, timezone, now): the heartbeat
 * anchors each series at the moment it was written, so only the server knows
 * the phase. A client-side guess would promise a date the scheduler won't
 * honor — e.g. an every-other-week cadence first fires 14 days out, not 7.
 */
export function formatNextSend(
	nextFireAt: string | null | undefined,
	timezone: string,
): string | null {
	if (!nextFireAt) return null;
	const parsed = new Date(nextFireAt);
	if (Number.isNaN(parsed.getTime())) return null;
	try {
		return new Intl.DateTimeFormat('en-US', {
			weekday: 'long',
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			timeZone: timezone,
		}).format(parsed);
	} catch {
		// An unknown IANA zone must not blank the panel.
		return new Intl.DateTimeFormat('en-US', {
			weekday: 'long',
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		}).format(parsed);
	}
}
